"use client"

import type { ButtonHTMLAttributes, ReactNode } from "react"
import { ChevronLeft } from "lucide-react"

interface CustomButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "disabled" | "back" | "lightback"
  loading?: boolean
  loadingText?: string
  children: ReactNode
}

export default function CustomButton({
  variant = "primary",
  loading = false,
  loadingText = "Loading...",
  children,
  className = "",
  disabled,
  ...props
}: CustomButtonProps) {
  const baseStyles =
    "w-full h-12 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"

  const variantStyles = {
    primary: "bg-blue-500 text-white hover:bg-blue-600 disabled:hover:bg-gray-200",
    secondary: "bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:hover:bg-gray-200",
    disabled: "bg-gray-200 text-gray-400 cursor-not-allowed",
    back: "bg-white text-black border border-gray-200 hover:bg-gray-50 disabled:hover:bg-gray-200",
    lightback: "bg-white text-gray-600 hover:text-gray-900 border-0 disabled:hover:bg-gray-200",
  }

  const isDisabled = disabled || loading || variant === "disabled"

  const buttonStyles = isDisabled
    ? `${baseStyles} bg-gray-200 text-gray-400 cursor-not-allowed !cursor-not-allowed`
    : `${baseStyles} ${variantStyles[variant]}`

  return (
    <button className={`${buttonStyles} ${className}`} disabled={isDisabled} {...props}>
      {loading ? (
        loadingText
      ) : (
        <>
          {variant === "lightback" && <ChevronLeft className="w-4 h-4" />}
          {children}
        </>
      )}
    </button>
  )
}
